
class DownloadService {
  constructor() {
    this.defaultOptions = {
      margin: 10,
      filename: 'teacher-list.pdf',
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { 
        scale: 2,
        useCORS: true,
        letterRendering: true
      },
      jsPDF: { 
        unit: 'mm', 
        format: 'a4', 
        orientation: 'portrait' 
      }
    };
  }

  // Helper method to create a properly rendered hidden container
  createHiddenContainer(htmlContent) {
    // Create a simple div container
    const container = document.createElement('div');
    
    // Set basic styles for proper rendering
    container.style.position = 'absolute';
    container.style.left = '-10000px';
    container.style.top = '-10000px';
    container.style.width = '800px';
    container.style.backgroundColor = 'white';
    container.style.padding = '20px';
    container.style.fontFamily = 'Arial, sans-serif';
    container.style.fontSize = '14px';
    container.style.lineHeight = '1.4';
    container.style.color = '#333';
    
    // Set the HTML content
    container.innerHTML = htmlContent;
    
    // Add to DOM
    document.body.appendChild(container);
    
    console.log('Hidden container created and added to DOM');
    return container;
  }

  // Helper method to wait for proper rendering
  async waitForRendering(container) {
    // Wait for DOM to be ready
    await new Promise(resolve => setTimeout(resolve, 50));
    
    // Force a reflow to ensure rendering
    const height = container.offsetHeight;
    console.log('Container height calculated:', height);
    
    // Wait for fonts to load
    if (document.fonts && document.fonts.ready) {
      await document.fonts.ready;
    }
    
    // Additional wait for complete rendering
    await new Promise(resolve => setTimeout(resolve, 200));
    
    console.log('Rendering wait completed');
  }

  // Helper method to cleanup container
  cleanupContainer(container) {
    if (container && container.parentNode) {
      document.body.removeChild(container);
      console.log('Container cleaned up');
    }
  }

  // Test function to verify html2pdf is working
  async testDownload() {
    try {
      // Import html2pdf dynamically (same as student download)
      const html2pdf = (await import('html2pdf.js')).default;

      // Create a very simple test element
      const testElement = document.createElement('div');
      testElement.innerHTML = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <style>
            body {
              font-family: 'Microsoft YaHei', 'SimHei', 'Arial Unicode MS', 'Noto Sans CJK SC', sans-serif;
              margin: 0;
              padding: 20px;
              background: white;
              font-size: 12px;
            }
            .header {
              text-align: center;
              margin-bottom: 20px;
            }
            .school-name {
              font-size: 24px;
              font-weight: bold;
              margin-bottom: 5px;
              color: #333;
            }
            .title {
              font-size: 20px;
              font-weight: bold;
              margin-bottom: 15px;
              color: #333;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="school-name">MyClass</div>
            <div class="title">測試 PDF 生成</div>
            <p style="color: #666; font-size: 16px; margin-bottom: 10px;">如果您看到這個 PDF，說明 html2pdf 庫工作正常。</p>
            <p style="color: #666; font-size: 14px;">生成時間: ${new Date().toLocaleString('zh-TW')}</p>
            <div style="margin-top: 20px; padding: 10px; background: #f0f0f0; border-radius: 5px;">
              <p style="color: #333; font-weight: bold;">測試內容</p>
              <p style="color: #666;">這是一個簡單的測試 PDF 文件</p>
            </div>
          </div>
        </body>
        </html>
      `;
      
      testElement.style.position = 'absolute';
      testElement.style.left = '-10000px';
      testElement.style.top = '-10000px';
      testElement.style.width = '800px';
      testElement.style.backgroundColor = 'white';
      
      document.body.appendChild(testElement);
      
      // Wait for rendering
      await new Promise(resolve => setTimeout(resolve, 300));
      
      const options = {
        margin: 10,
        filename: 'test-download.pdf',
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 1,
          useCORS: true,
          letterRendering: true
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4', 
          orientation: 'portrait' 
        }
      };

      console.log('Starting test PDF generation...');
      await html2pdf().set(options).from(testElement).save();
      console.log('Test PDF generation completed');
      
      // Clean up
      document.body.removeChild(testElement);

      return { success: true };
    } catch (error) {
      console.error('Test download failed:', error);
      throw error;
    }
  }

  // Test function with hardcoded teacher data
  async testDownloadWithHardcodedData() {
    try {
      console.log('=== TESTING WITH HARDCODED DATA ===');
      
      // Import html2pdf dynamically
      const html2pdf = (await import('html2pdf.js')).default;

      // Create hardcoded test data
      const testTeacherData = {
        totalTeachers: 3,
        totalSubjects: 2,
        seniorTeachers: 1,
        teachers: [
          {
            name: '陳嘉俊',
            teacher_id: 'tn_01-t001',
            subject_specialty: 'Mathematics',
            status: '在職'
          },
          {
            name: '陳廣隆',
            teacher_id: 'tn_01-t002',
            subject_specialty: 'English',
            status: '在職'
          },
          {
            name: '陳柏輝',
            teacher_id: 'tn_01-t003',
            subject_specialty: 'Science',
            status: '在職'
          }
        ]
      };

      console.log('Hardcoded test data:', testTeacherData);

      // Generate HTML content
      const htmlContent = this.generateTeacherListHTML(testTeacherData);
      console.log('Generated HTML content length:', htmlContent.length);
      
      // Create element and append to DOM
      const element = document.createElement('div');
      element.innerHTML = htmlContent;
      element.style.position = 'absolute';
      element.style.left = '-10000px';
      element.style.top = '-10000px';
      element.style.width = '800px';
      element.style.backgroundColor = 'white';
      
      document.body.appendChild(element);
      
      // Wait for rendering
      await new Promise(resolve => setTimeout(resolve, 300));

      // Generate PDF
      const options = {
        margin: 10,
        filename: 'test-hardcoded-teachers.pdf',
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 1,
          useCORS: true,
          letterRendering: true
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4', 
          orientation: 'portrait' 
        }
      };

      console.log('Starting hardcoded PDF generation...');
      await html2pdf().set(options).from(element).save();
      console.log('Hardcoded PDF generation completed');

      // Clean up
      document.body.removeChild(element);

      return { success: true };
    } catch (error) {
      console.error('Hardcoded test download failed:', error);
      throw error;
    }
  }

  // Download teacher list as PDF - EXACT COPY OF WORKING STUDENT DOWNLOAD
  async downloadTeacherList(teacherData, filename = 'teacher-list.pdf') {
    try {
      console.log('=== TEACHER DOWNLOAD (COPYING WORKING STUDENT CODE) ===');
      
      // Get simple teacher data - just name and subject
      const teachers = teacherData?.teachers || [];
      console.log('Teachers count:', teachers.length);
      
      if (teachers.length === 0) {
        alert('沒有教師資料可供下載');
        return;
      }

      // Import html2pdf dynamically with multiple fallback approaches
      let html2pdf;
      try {
        console.log('=== HTML2PDF IMPORT DEBUG ===');
        const html2pdfModule = await import('html2pdf.js');
        console.log('html2pdfModule:', html2pdfModule);
        console.log('html2pdfModule keys:', Object.keys(html2pdfModule));
        console.log('html2pdfModule.default:', html2pdfModule.default);
        
        // Try different ways to get the function
        html2pdf = html2pdfModule.default || html2pdfModule.html2pdf || html2pdfModule;
        console.log('html2pdf after fallback:', html2pdf);
        console.log('html2pdf type:', typeof html2pdf);
        
        if (!html2pdf || typeof html2pdf !== 'function') {
          console.error('html2pdf is not a function!', html2pdf);
          throw new Error('html2pdf import failed - not a function');
        }
        
        console.log('html2pdf() result:', html2pdf());
        console.log('html2pdf() type:', typeof html2pdf());
      } catch (importError) {
        console.error('Import error:', importError);
        throw new Error('無法載入PDF生成庫: ' + importError.message);
      }

      // Create HTML content with proper Chinese font support (EXACT COPY FROM WORKING STUDENT CODE)
      const exportDate = new Date().toLocaleDateString('en-HK');
      
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <style>
            body {
              font-family: 'Microsoft YaHei', 'SimHei', 'Arial Unicode MS', 'Noto Sans CJK SC', sans-serif;
              margin: 0;
              padding: 20px;
              background: white;
              font-size: 12px;
            }
            .header {
              text-align: center;
              margin-bottom: 20px;
            }
            .school-name {
              font-size: 24px;
              font-weight: bold;
              margin-bottom: 5px;
              color: #333;
            }
            .title {
              font-size: 20px;
              font-weight: bold;
              margin-bottom: 15px;
              color: #333;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
            }
            th {
              background-color: #4285f4;
              color: white;
              padding: 10px;
              text-align: left;
              font-weight: bold;
              border: 1px solid #ddd;
            }
            td {
              padding: 8px 10px;
              border: 1px solid #ddd;
            }
            tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            .footer {
              text-align: center;
              margin-top: 30px;
              font-size: 10px;
              color: #666;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="school-name">MyClass</div>
            <div class="title">教師名單</div>
            <div class="date">生成日期: ${exportDate}</div>
          </div>
          
          <table>
            <thead>
              <tr>
                <th>教師編號</th>
                <th>教師姓名</th>
                <th>任教科目</th>
              </tr>
            </thead>
            <tbody>
              ${teachers.map((teacher, index) => `
                <tr>
                  <td>${teacher.teacher_no || (index + 1)}</td>
                  <td>${teacher.name || '未知教師'}</td>
                  <td>${teacher.subject_specialty || '未指定'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          
          <div class="footer">
            共 ${teachers.length} 位教師 | 由 MyClass 系統生成
          </div>
        </body>
        </html>
      `;

      // Create a hidden iframe to properly render the HTML (EXACT COPY FROM WORKING STUDENT CODE)
      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.left = '-9999px';
      iframe.style.top = '-9999px';
      iframe.style.width = '210mm';
      iframe.style.height = '297mm';
      iframe.style.border = 'none';
      document.body.appendChild(iframe);

      // Write HTML content to iframe (EXACT COPY FROM WORKING STUDENT CODE)
      iframe.contentDocument.open();
      iframe.contentDocument.write(htmlContent);
      iframe.contentDocument.close();

      // Wait for iframe to load and render (EXACT COPY FROM WORKING STUDENT CODE)
      await new Promise((resolve) => {
        iframe.onload = resolve;
        setTimeout(resolve, 1000);
      });

      // Get the body element from iframe (EXACT COPY FROM WORKING STUDENT CODE)
      const element = iframe.contentDocument.body;

      // Configure PDF options (EXACT COPY FROM WORKING STUDENT CODE)
      const options = {
        margin: [10, 10, 10, 10],
        filename: filename,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          letterRendering: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          width: 794,
          height: 1123
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4', 
          orientation: 'portrait',
          compress: true
        }
      };

      // Generate and download PDF (EXACT COPY FROM WORKING STUDENT CODE)
      console.log('=== BEFORE PDF GENERATION ===');
      console.log('html2pdf function:', html2pdf);
      console.log('html2pdf type:', typeof html2pdf);
      console.log('element:', element);
      console.log('element type:', typeof element);
      console.log('options:', options);
      
      const pdfGenerator = html2pdf();
      console.log('pdfGenerator created:', pdfGenerator);
      console.log('pdfGenerator type:', typeof pdfGenerator);
      console.log('pdfGenerator.set:', typeof pdfGenerator.set);
      console.log('pdfGenerator.from:', typeof pdfGenerator.from);
      
      await pdfGenerator.set(options).from(element).save();
      
      // Clean up (EXACT COPY FROM WORKING STUDENT CODE)
      document.body.removeChild(iframe);
      
      console.log('Teacher PDF generation completed successfully');
      return { success: true };
      
    } catch (error) {
      console.error('Error generating teacher PDF:', error);
      throw error;
    }
  }

  // Download subject teachers as PDF
  async downloadSubjectTeachers(subjectData, filename = 'subject-teachers.pdf') {
    try {
      console.log('Download service received subject data:', subjectData);
      
      // Import html2pdf dynamically
      const html2pdf = (await import('html2pdf.js')).default;
      
      const htmlContent = this.generateSubjectTeachersHTML(subjectData);
      console.log('Generated subject HTML content length:', htmlContent.length);
      
      const tempContainer = this.createHiddenContainer(htmlContent);
      await this.waitForRendering(tempContainer);

      const options = {
        ...this.defaultOptions,
        filename: filename
      };

      console.log('Starting subject PDF generation...');
      await html2pdf().set(options).from(tempContainer).save();
      console.log('Subject PDF generation completed');

      this.cleanupContainer(tempContainer);

      return { success: true };
    } catch (error) {
      console.error('Error generating subject PDF:', error);
      throw error;
    }
  }

  // Download committee members as PDF
  async downloadCommitteeMembers(groupData, filename = 'committee-members.pdf') {
    try {
      console.log('Download service received group data:', groupData);
      
      // Import html2pdf dynamically
      const html2pdf = (await import('html2pdf.js')).default;
      
      const htmlContent = this.generateCommitteeMembersHTML(groupData);
      console.log('Generated committee HTML content length:', htmlContent.length);
      
      const tempContainer = this.createHiddenContainer(htmlContent);
      await this.waitForRendering(tempContainer);

      const options = {
        ...this.defaultOptions,
        filename: filename
      };

      console.log('Starting committee PDF generation...');
      await html2pdf().set(options).from(tempContainer).save();
      console.log('Committee PDF generation completed');

      this.cleanupContainer(tempContainer);

      return { success: true };
    } catch (error) {
      console.error('Error generating committee PDF:', error);
      throw error;
    }
  }

  // Generate teacher table HTML
  generateTeacherTableHTML(teachers) {
    return teachers.map((teacher, index) => `
      <tr>
        <td>${index + 1}</td>
        <td>${teacher.teacher_id || 'N/A'}</td>
        <td>${teacher.name || 'N/A'}</td>
        <td>${teacher.subject_specialty || 'N/A'}</td>
        <td>${teacher.status || 'Active'}</td>
      </tr>
    `).join('');
  }

  // Generate HTML for teacher list
  generateTeacherListHTML(teacherData) {
    const currentDate = new Date().toLocaleDateString('en-HK');
    const teachers = teacherData.teachers || [];

    console.log('=== HTML GENERATION DEBUG ===');
    console.log('teacherData received:', teacherData);
    console.log('teachers array:', teachers);
    console.log('teachers length:', teachers.length);
    console.log('teachers type:', typeof teachers);
    console.log('Is array:', Array.isArray(teachers));

    if (teachers.length > 0) {
      console.log('First teacher in HTML generation:', teachers[0]);
      console.log('Sample teacher fields:', {
        name: teachers[0].name,
        teacher_id: teachers[0].teacher_id,
        subject_specialty: teachers[0].subject_specialty,
        status: teachers[0].status
      });
      
      // Test HTML generation with first few teachers
      const testTeachers = teachers.slice(0, 3);
      console.log('Testing HTML generation with first 3 teachers:', testTeachers);
      
      const testHTML = this.generateTeacherTableHTML(testTeachers);
      console.log('Generated test HTML length:', testHTML.length);
      console.log('Generated test HTML preview:', testHTML.substring(0, 500));
    }
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <style>
          body {
            font-family: 'Microsoft YaHei', 'SimHei', 'Arial Unicode MS', 'Noto Sans CJK SC', sans-serif;
            margin: 0;
            padding: 20px;
            background: white;
            font-size: 12px;
          }
          .header {
            text-align: center;
            margin-bottom: 20px;
          }
          .school-name {
            font-size: 24px;
            font-weight: bold;
            margin-bottom: 5px;
            color: #333;
          }
          .school-subtitle {
            font-size: 16px;
            margin-bottom: 10px;
            color: #666;
          }
          .title {
            font-size: 20px;
            font-weight: bold;
            margin-bottom: 15px;
            color: #333;
          }
          .stats {
            display: flex;
            justify-content: space-between;
            background: #f7fafc;
            padding: 15px;
            border-radius: 8px;
            margin-bottom: 20px;
          }
          .stat-item {
            text-align: center;
          }
          .stat-number {
            font-size: 24px;
            color: #667eea;
            font-weight: bold;
          }
          .stat-label {
            color: #4a5568;
            font-size: 12px;
          }
          .section-title {
            color: #2d3748;
            border-bottom: 2px solid #667eea;
            padding-bottom: 10px;
            margin-bottom: 20px;
            font-size: 16px;
            font-weight: bold;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
            background: white;
          }
          th {
            background-color: #4285f4;
            color: white;
            padding: 8px;
            text-align: left;
            font-weight: bold;
            border: 1px solid #ddd;
            font-size: 11px;
          }
          td {
            padding: 6px 8px;
            border: 1px solid #ddd;
            text-align: left;
            font-size: 10px;
            color: #333;
          }
          tr:nth-child(even) {
            background-color: #f5f5f5;
          }
          tr:nth-child(odd) {
            background-color: white;
          }
          .footer {
            font-size: 10px;
            color: #666;
            text-align: center;
            margin-top: 20px;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="school-name">MyClass</div>
          <div class="school-subtitle">Real School A</div>
          <div class="title">Teacher List</div>
          <div style="font-size: 14px; margin-bottom: 20px; text-align: left; color: #555;">
            <div>Export Date: ${currentDate}</div>
          </div>
        </div>
        
        <div class="stats">
          <div class="stat-item">
            <div class="stat-number">${teacherData.totalTeachers || 0}</div>
            <div class="stat-label">總教師數</div>
          </div>
          <div class="stat-item">
            <div class="stat-number">${teacherData.totalSubjects || 0}</div>
            <div class="stat-label">教學科目</div>
          </div>
          <div class="stat-item">
            <div class="stat-number">${teacherData.seniorTeachers || 0}</div>
            <div class="stat-label">資深教師</div>
          </div>
        </div>

        <h2 class="section-title">教師詳細資料</h2>

        ${teachers.length > 0 ? `
          <table>
            <thead>
              <tr>
                <th>No</th>
                <th>Teacher ID</th>
                <th>Name</th>
                <th>Subject Specialty</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              ${this.generateTeacherTableHTML(teachers)}
            </tbody>
          </table>
        ` : `
          <div style="text-align: center; padding: 40px; color: #718096;">
            <p>暫無教師資料</p>
          </div>
        `}
        
        <div class="footer">
          Generated by MyClass System
        </div>
      </body>
      </html>
    `;
  }

  // Generate HTML for subject teachers
  generateSubjectTeachersHTML(subjectData) {
    const currentDate = new Date().toLocaleDateString('zh-TW');
    
    return `
      <div style="font-family: 'Microsoft JhengHei', Arial, sans-serif; padding: 20px; max-width: 800px; margin: 0 auto;">
        <div style="text-align: center; margin-bottom: 30px;">
          <h1 style="color: #2d3748; margin: 0; font-size: 24px;">科目教師名單</h1>
          <p style="color: #718096; margin: 5px 0; font-size: 14px;">生成日期: ${currentDate}</p>
        </div>

        ${subjectData.map(subject => `
          <div style="margin-bottom: 30px;">
            <h2 style="color: #2d3748; border-bottom: 2px solid #667eea; padding-bottom: 10px; margin-bottom: 15px;">
              ${subject.name} (${subject.teachers.length} 位教師)
            </h2>
            
            <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 10px;">
              ${subject.teachers.map(teacher => `
                <div style="border: 1px solid #e2e8f0; border-radius: 6px; padding: 12px; background: white;">
                  <div style="display: flex; align-items: center; margin-bottom: 8px;">
                    <div style="width: 30px; height: 30px; border-radius: 50%; background: #667eea; display: flex; align-items: center; justify-content: center; color: white; font-weight: bold; margin-right: 10px; font-size: 12px;">
                      ${teacher.name.charAt(0)}
                    </div>
                    <div>
                      <div style="font-weight: bold; color: #2d3748; font-size: 14px;">${teacher.name}</div>
                      ${teacher.role ? `<div style="color: #667eea; font-size: 12px; background: #f0f4ff; padding: 2px 6px; border-radius: 4px; display: inline-block;">${teacher.role}</div>` : ''}
                    </div>
                  </div>
                  <div style="color: #718096; font-size: 12px;">
                    ${teacher.subject_specialty || '未指定'}
                  </div>
                </div>
              `).join('')}
            </div>
          </div>
        `).join('')}
      </div>
    `;
  }

  // Generate HTML for committee members
  generateCommitteeMembersHTML(groupData) {
    const currentDate = new Date().toLocaleDateString('zh-TW');
    
    return `
      <div style="font-family: 'Microsoft JhengHei', Arial, sans-serif; padding: 20px; max-width: 800px; margin: 0 auto;">
        <div style="text-align: center; margin-bottom: 30px;">
          <h1 style="color: #2d3748; margin: 0; font-size: 24px;">組別成員名單</h1>
          <p style="color: #718096; margin: 5px 0; font-size: 14px;">生成日期: ${currentDate}</p>
        </div>

        ${Object.entries(groupData).map(([categoryKey, category]) => `
          <div style="margin-bottom: 40px;">
            <h2 style="color: #2d3748; border-bottom: 2px solid #667eea; padding-bottom: 10px; margin-bottom: 20px;">
              ${category.name}
            </h2>
            
            ${category.groups.map(group => `
              <div style="margin-bottom: 25px;">
                <h3 style="color: #4a5568; font-size: 16px; margin-bottom: 10px;">
                  ${group.chineseName} (${group.members?.length || 0} 人)
                </h3>
                
                <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 8px;">
                  ${(group.members || []).map(member => `
                    <div style="border: 1px solid #e2e8f0; border-radius: 6px; padding: 10px; background: white;">
                      <div style="display: flex; align-items: center; margin-bottom: 6px;">
                        <div style="width: 25px; height: 25px; border-radius: 50%; background: #667eea; display: flex; align-items: center; justify-content: center; color: white; font-weight: bold; margin-right: 8px; font-size: 11px;">
                          ${member.name.charAt(0)}
                        </div>
                        <div>
                          <div style="font-weight: bold; color: #2d3748; font-size: 13px;">${member.name}</div>
                          ${member.role ? `<div style="color: #667eea; font-size: 10px; background: #f0f4ff; padding: 1px 4px; border-radius: 3px; display: inline-block;">${member.role}</div>` : ''}
                        </div>
                      </div>
                      <div style="color: #718096; font-size: 11px;">
                        ${member.subject_specialty || '未指定'}
                      </div>
                    </div>
                  `).join('')}
                </div>
              </div>
            `).join('')}
          </div>
        `).join('')}
      </div>
    `;
  }
}

export default new DownloadService();
